<template>
	<div ref="fileUploadRef" class="relative isolate rem:min-h-[42px]">
		<div v-if="isOverDropZone" :class="context.classes.dropZone">
			<NuxtIcon class="w-6 text-icon-active" name="custom/upload" />
			Přetáhněte soubory sem
		</div>
		<template v-else>
			<ul v-if="visibleValidateErrors" class="px-3 pt-2">
				<li v-for="(error, index) in validateErrors" :key="index" class="flex gap-3 items-center py-2 border-b border-border-tertiary">
					<div class="relative">
						<NuxtIcon class="text-xl text-icon-tertiary" name="custom/file" filled />
						<NuxtIcon
							class="text-xs text-icon-inverse inline-flex rounded-full absolute right-0 bottom-0 translate-x-1/4 translate-y-1/3 rem:size-[14px] items-center justify-center bg-functional-danger-primary"
							name="x"
							filled
						/>
					</div>
					<span class="typo-body-caption min-w-[0px] text-functional-error-primary">
						{{ error.value }}
					</span>
					<button
						type="button"
						class="btn btn-sm btn-secondary ml-auto"
						@click="handleValidationErrorsByKey(error.key)"
					>
						Ok
					</button>
				</li>
			</ul>
			<div v-else-if="context._value.length">
				<ul class="px-3 py-2">
					<li v-for="(file, index) in context._value" :key="index" class="border-b border-border-tertiary">
						<div class="flex gap-3 items-center py-2">
							<div class="relative">
								<NuxtIcon class="text-xl text-icon-tertiary" name="custom/file" filled />
								<NuxtIcon
									class="text-xs text-icon-inverse inline-flex rounded-full absolute right-0 bottom-0 translate-x-1/4 translate-y-1/3 rem:size-[14px] items-center justify-center"
									:class="validateFileAccept(file, context.attrs.accept) ? 'bg-functional-success-primary' : 'bg-functional-error-primary'"
									:name="validateFileAccept(file, context.attrs.accept) ? 'check' : 'x'"
									filled
								/>
							</div>
							<div class="typo-body-caption min-w-[0px]">
								<div class="text-body-primary truncate max-w-full">
									{{ file.name }}
								</div>
								<div class="text-body-quaternary">
									{{ filesize(file.size, {standard: 'jedec'}) }}, {{ formatFileType(file.name) }}
								</div>
							</div>
							<button v-if="validateFileAccept(file, context.attrs.accept)" type="button" class="ml-auto" @click="removeFile(file)">
								<NuxtIcon class="text-functional-error-primary text-base" name="trash-2" filled />
							</button>
							<button v-else type="button" class="btn btn-sm btn-secondary ml-auto" @click="removeFile(file)">
								Ok
							</button>
						</div>
						<div v-if="!validateFileAccept(file, context.attrs.accept)" class="typo-body-caption text-functional-error-primary mb-1.5">
							Tento formát nepodporujeme. Použijte tyto formáty: <span class="text-body-primary" v-html="accepFileTypes.map((type: string) => '.' + type).join(', ')" />.
						</div>
					</li>
				</ul>
				<div v-if="!visibleValidateErrors" class="text-center flex items-center justify-center flex-col pb-2">
					<span class="typo-body-caption text-brand-globus-green">
						{{ uploadedFileSizes }}
					</span>

					<span v-if="canUploadMoreFiles" class="typo-body-caption text-body-tertiary">
						Můžete nahrát ještě {{ t('plural.filesNumber', remainingFileUploadLimit ?? 0) }} (Max {{ filesize(remainingFileUploadSize, {standard: 'jedec'}) }}).
					</span>
					<span v-else class="typo-body-caption text-body-tertiary">
						Byl nahrán maximalní počet souborů
					</span>
				</div>
			</div>
			<div v-if="visibleValidateErrors || canUploadMoreFiles" :class="context.classes.selectFile" @click="triggerFileInput">
				<span class="flex items-center border-r border-border-secondary self-stretch">
					<NuxtIcon class="text-xl text-icon-active pr-2" name="custom/upload" filled />
					<span v-if="!isMobileOrTablet" class="typo-body-s pr-2">Přetáhněte soubory sem</span>
				</span>
				<span class="font-semibold underline pl-2" :class="{'flex-1 text-center': !isMobileOrTablet}">
					vybrat soubory
				</span>
			</div>
		</template>

		<input
			:id="context.id"
			ref="fileInputRef"
			type="file"
			:class="context.classes.input"
			:multiple="context.attrs.multiple"
			:accept="context.attrs.accept"
			@change="onFileChange"
		/>
	</div>
</template>

<script setup lang="ts">
	import {useDropZone} from '@vueuse/core';
	import {filesize} from 'filesize';
	import type {FormKitMessageProps} from '@formkit/core';
	import type {FileUploadContext} from '~/types/formkitFileUpload';
	import {validateFileAccept} from '~/utils/files';

	interface Props {
		context: FileUploadContext
	}
	const props = defineProps<Props>();

	const {isMobileOrTablet} = useDevice();
	const {t} = useI18n();
	const visibleDropZone = ref(false);
	const fileUploadRef = ref<HTMLElement | null>(null);
	const fileInputRef = ref<HTMLInputElement | null>(null);
	const {isOverDropZone} = useDropZone(fileUploadRef, {
		onDrop(files: File[] | null) {
			if (files) {
				const inputEvent = new Event('change');
				Object.defineProperty(inputEvent, 'target', {writable: false, value: {files}});
				onFileChange(inputEvent);
			}
		},
		onEnter() {
			visibleDropZone.value = true;
		},
		onLeave() {
			visibleDropZone.value = false;
		},
	});

	const triggerFileInput = () => {
		if (validateErrors.value.length) {
			removeAllFiles();
		}
		fileInputRef.value?.click();
	};

	const onFileChange = (event: Event) => {
		const input = event.target as HTMLInputElement;
		if (input.files) {
			const newFiles = Array.from(input.files);
			const existingFiles = props.context._value;

			props.context.attrs.multiple ? handleFiles([...existingFiles, ...newFiles]) : handleFiles(newFiles);
		}
	};

	const handleFiles = (files: File[]) => {
		props.context.node.input(files);
	};

	const accepFileTypes = computed(() => {
		return props.context.attrs.accept?.split(',').map((type) => {
			if (type.startsWith('.')) {
				return type.slice(1);
			}
			return type;
		});
	});

	const maxFileSizeInBytes = computed(() => props.context.attrs['max-file-size'] * 1024 * 1024);
	const uploadedFileSizes = computed(() => `${t('plural.files', props.context._value.length ?? 0)} se uspěšně ${t('plural.fileUpload', props.context._value.length ?? 0)} (${filesize(totalFilesSize.value, {standard: 'jedec'})})`);
	const totalFilesSize = computed(() => props.context._value.reduce((acc, file) => acc + file.size, 0));
	const remainingFileUploadLimit = computed(() => props.context.attrs['max-files'] - props.context._value.length);
	const canUploadMoreFiles = computed(() => remainingFileUploadLimit.value > 0 || props.context._value.length < props.context.attrs['max-files']);
	const remainingFileUploadSize = computed(() => maxFileSizeInBytes.value - totalFilesSize.value);

	const validateRules = ['rule_validateMaxFiles', 'rule_validateMaxFileSize'];
	const validateErrors = computed(() => {
		const messages = props.context.node.context?.messages;
		return messages
			? Object.values(messages)
				.filter((message: FormKitMessageProps) => validateRules.includes(message.key))
				.map((message: FormKitMessageProps) => ({key: message.key, value: message.value}))
			: [];
	});
	const visibleValidateErrors = computed(() => validateErrors.value.length);

	function formatFileType(fileName: string): string {
		const parts = fileName.split('.');
		const extension = parts[parts.length - 1];
		return extension;
	}

	function removeFile(file: File) {
		const index = props.context._value.indexOf(file);
		if (index > -1) {
			const newValue = [...props.context._value];
			newValue.splice(index, 1);
			props.context.node.input(newValue);
		}
	}

	function removeAllFiles() {
		props.context.node.input([]);
	}

	function handleValidationErrorsByKey(key: FormKitMessageProps['key']) {
		switch (key) {
			case 'rule_validateMaxFiles': {
				const maxFiles = props.context.attrs['max-files'];
				props.context.node.input(props.context._value.slice(0, maxFiles));
				break;
			}
			case 'rule_validateMaxFileSize': {
				let totalSize = 0;
				const newValue = props.context._value.filter((file) => {
					totalSize += file.size;
					return totalSize <= maxFileSizeInBytes.value;
				});
				props.context.node.input(newValue);
				break;
			}
			default:
				removeAllFiles();
		}
	}
</script>
